import React from 'react'
import Zoom from 'react-medium-image-zoom'
import styled, { css } from 'styled-components'

import { assignPrefix } from '../../../constants'
import { defaultTheme as theme } from '../../../theme'
import { mobile, tablet } from '../../../utils/styles/media'

import 'react-medium-image-zoom/dist/styles.css'

export const Container = styled.div`
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  ${mobile(css`
    padding: 0 20px;
  `)};
`

export const Article = styled.article`
  word-break: break-word;
  word-wrap: break-word;
  iframe {
    margin-top: 32px;
    width: 100%;
  }
`

export const SocialFooter = styled.div`
  padding: 100px 0;
  h3 {
    font-weight: 700;
    font-size: 36px;
    line-height: 110%;
    letter-spacing: -0.02em;
    color: ${theme.colors.gray[800]};
    margin-bottom: 16px;
  }
  p {
    font-weight: 400;
    font-size: 18px;
    margin-bottom: 24px;
    line-height: 140%;
    color: ${theme.colors.gray[700]};
  }
`

const bodyTextStyle = css`
  font-size: 18px;
  line-height: 1.7;
  color: ${theme.colors.text};
  ${mobile(css`
    font-size: 17px;
  `)};
`

const listStyles = css`
  padding: 0;
  list-style: none;
  list-style-image: none;
  margin-top: 8px;
`

export const Ul = styled.ul`
  ${listStyles};
  li {
    &:before {
      color: ${theme.colors.black};
      content: '•';
      font-size: 24px;
      line-height: 28px;
      vertical-align: middle;
      padding-right: 8px;
    }
  }
`

export const Ol = styled.ol`
  padding-inline-start:0;
`

export const Li = styled.li`
  ${bodyTextStyle};
  margin-left: 20px;
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
  &:before {
    position: absolute;
    display: inline-block;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    width: 78px;
    margin-left: -78px;
    text-align: right;
  }
  pre {
    margin-top: 8px !important;
  }
`

export const Paragraph = styled.p`
  margin: 0;
  margin-top: 29px;
  ${bodyTextStyle};
  & + ${Ol}, & + ${Ul}, & + p {
    ${mobile(css`
      margin-top: 21px;
    `)};
  }
  ${Li} & + ${Ol}, ${Li} & + ${Ul}, ${Li} & + p {
    margin-top: 12px;
    margin-bottom: 40px;
    ${mobile(css`
      margin-top: 6px;
    `)};
  }
`

export const Blockquote = styled.blockquote`
  margin: 1em 0;
  font-size: 18px;
  line-height: 1.6;
  color: ${theme.colors.textSecondary};
  font-style: italic;
  border-left: 6px solid ${theme.colors.textSecondary};
  padding-left: 20px;
  padding-bottom: 2px;
  margin-top: 40px;
  margin-bottom: 40px;
  ${mobile(css`
    padding-left: 16px;
    margin-left: -20px;
    margin-top: 21px;
    margin-bottom: 21px;
  `)};
  ${Paragraph}:last-child {
    margin-bottom: 0;
  }
  * {
    color: ${theme.colors.textSecondary} !important;
  }
`

const internalLink = /^https?:\/\/(?:www\.)?prisma\.io(?:\/|$)/
const isExternalLink = (href?: string) => {
  if (typeof href !== 'string') return false
  if (href.startsWith('#')) return false
  if (href.startsWith('/')) return false
  if (internalLink.test(href)) return false
  return true
}

export const A = styled.a.attrs({
  target: ({ href }) => (isExternalLink(href) ? '_blank' : undefined),
  rel: ({ href }) => (isExternalLink(href) ? 'noopener noreferrer' : undefined),
})`
  font-size: inherit;
  color: ${theme.colors.text};
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.68) 50%, rgba(0, 0, 0, 0) 50%);
  background-repeat: repeat-x;
  background-size: 2px 0.1em;
  background-position: 0 1.13em;
  code {
    position: relative;
    text-decoration: underline;
    z-index: 1;
    &::after {
      z-index: -1;
      content: '';
      position: absolute;
      background: white;
      left: -2px;
      right: -2px;
      height: 4px;
      bottom: 3px;
    }
  }
`

const autoAnchorStyle = css`
  position: relative;
  display: block;
  align-items: center;
  & a.autolink-headers-anchor {
    display: block;
    opacity: 0;
    position: absolute;
    left: -2rem;
    top: 50%;
    margin-top: -1rem;
    padding: 0.5rem;
    text-decoration: none;
    background: transparent;
    transition: opacity 0.125s ease;
    ${mobile(
      css`
        display: none;
      `,
    )}
  }
  &:hover a.autolink-headers-anchor {
    opacity: 1;
  }
  & > svg[aria-hidden='true'] {
    display: none;
  }
  & a.autolink-headers-anchor svg {
    display: block;
    fill: currentColor;
  }
`

export const H5 = styled.h5`
  ${autoAnchorStyle}
  font-family: ${theme.fonts.text};
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color:${theme.colors.text};
  margin-top: 32px;
  margin-bottom: 24px;
  ${mobile(css`
    font-size: 20px;
    margin-top: 16px;
    margin-bottom: 12px;
  `)}
  & + ${Paragraph} {
    margin-top: 0;
  }
`

export const H4 = styled.h4`
  ${autoAnchorStyle}
  font-family: ${theme.fonts.text};
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
  color: ${theme.colors.text};
  margin-top: 32px;
  margin-bottom: 32px;
  ${mobile(css`
    font-size: 20px;
    margin-top: 16px;
    margin-bottom: 12px;
  `)}
  & + ${Paragraph} {
    margin-top: 0;
  }
`

export const H3 = styled.h3`
  ${autoAnchorStyle}
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  color: ${theme.colors.text};
  margin-top: 40px;
  margin-bottom: 32px;
  ${mobile(css`
    font-size: 24px;
    margin-top: 24px;
    margin-bottom: 14px;
  `)}
  & + ${Paragraph} {
    margin-top: 0;
  }
`

export const H2 = styled.h2`
  ${autoAnchorStyle}
  font-size: 40px;
  line-height: 1.23;
  font-weight: bold;
  color: ${theme.colors.text};
  margin-top: 64px;
  margin-bottom: 32px;
  ${mobile(css`
    font-size: 28px;
    margin-top: 48px;
    margin-bottom: 24px;
  `)}
  & + ${Paragraph} {
    margin-top: 0;
  }
  & + ${H3} {
    margin-top: 0;
  }
`
// this is just to ensure we don't use H1s in MDX content because they are reserved for the blogpost title
export const H1Error = styled.h1`
  font-family: 'Times New Roman', serif;
  background: red;
  font-size: 120px;
`

export const Hr = styled.hr`
  margin: 48px auto;
  display: block;
  border: 0;
  height: 4px;
  width: 120px;
  background-color: ${theme.colors.gray[200]};
  border-radius: 4px;
`

export const Pre = styled.pre`
  background: ${theme.colors.gray[100]} !important;
  border-radius: ${p => p.theme.radius}px;
  padding: 20px !important;
  font-size: 14px;
  line-height: 24px !important;
  color: ${theme.colors.text};
  overflow: auto;
  margin-top: 40px !important;
  margin-bottom: 40px !important;
  code {
    font-size: 14px;
    line-height: 24px !important;
    text-shadow: none;
    background: none;
    font-family: ${p => p.theme.fontStackMono};
  }
  ${mobile(css`
    margin-top: 20px !important;
    margin-bottom: 20px !important;
  `)};
`

export const InlineCode = styled.code`
  background: ${theme.colors.gray[200]};
  border-radius: ${p => p.theme.radiusSmall}px;
  padding: 0.2em 0.4em;
  /* margin: 0 0.275em; */
  margin: 0;
  font-size: 0.875em;
  line-height: 1;
  a {
    background-image: none;
  } /* Lol.. */
`

export const Strong = styled.strong`
  font-weight: 600;
`

export const TableWrapper = styled.div`
  ${mobile(css`
    width: 100%;
    overflow-x: auto;
  `)};
`

export const Table = styled.table`
  width: 100%;
  border: 1px solid ${theme.colors.gray[300]};
  border-radius: 8px;
  border-spacing: 0;
  margin: 1.5rem 0;
  word-break: normal;
`

export const TH = styled.th`
  padding: 12px 24px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
`

export const TD = styled.td`
  padding: 16px 24px;
  border-top: 1px solid ${theme.colors.gray[300]};
  text-align: left;
  color: ${theme.colors.text};
`

const WideImageComponent = (props: any) => {
  const src = assignPrefix(props.src)
  const imgProps = {
    ...props,
    src,
  }
  return <Zoom><img {...imgProps} /></Zoom>
}

export const WideImage = styled(WideImageComponent)`
  display: block;
  width: calc(100% + 300px);
  height: auto;
  margin: 24px -150px;
  cursor: pointer;
  ${tablet(css`
    width: 100%;
    margin: 20px 0;
  `)};
`
